/**
 * @file RevenueMarginTab.tsx
 * @brief Super Admin → Analytics → Revenue & margin (W22), from B25 `margin`.
 *
 * @details
 * Revenue and cost over time (lines, never below 0), revenue vs cost per
 * product, gross margin per product (a loss is a bar left of zero, e.g. Ask),
 * and the exact table. Plan-covered products carry their cost against product
 * revenue 0, so the help text says to read them with the total.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { Fragment, useMemo } from "react";
import { GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { SkeletonRows } from "@/components/ui/Skeleton";
import {
  formatCount,
  formatMoney,
  formatMoneyTick,
  formatPct,
  notConvertedLines,
  NULL_REASONS,
  periodLabel,
  pick,
  planLabel,
} from "../businessMath";
import type { Currency, Margin } from "../types";
import { ChartLegend, DivergingBars, LinesOverTime, PairedBars, type ChartSeries } from "../components/charts";
import {
  DataTable,
  EmptyNote,
  ErrorNote,
  MoneyText,
  NullValue,
  SectionHeading,
  tdClass,
  tdNumClass,
} from "../components/common";

const REV_COST: ChartSeries[] = [
  { key: "revenue", label: "Revenue", color: "var(--viz-1)" },
  { key: "cost", label: "Cost", color: "var(--viz-2)" },
];

export function RevenueMarginTab({
  data,
  isLoading,
  error,
  currency,
}: {
  data: Margin | undefined;
  isLoading: boolean;
  error: unknown;
  currency: Currency;
}) {
  const fmt = (v: number) => formatMoney(v, currency);
  const tick = (v: number) => formatMoneyTick(v, currency);

  const seriesRows = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        label: periodLabel(s.period, data!.window.groupBy),
        revenue: Math.max(0, pick(s.revenue, currency) ?? 0),
        cost: Math.max(0, pick(s.cost, currency) ?? 0),
        margin: pick(s.gross_margin, currency),
        margin_pct: s.gross_margin_pct,
      })),
    [data, currency],
  );
  const anyMoney = seriesRows.some((r) => r.revenue > 0 || r.cost > 0);

  const products = useMemo(
    () => (data?.products ?? []).filter((p) => p.revenue.gbp !== 0 || p.cost.gbp !== 0 || p.payments > 0),
    [data],
  );

  if (error) return <ErrorNote error={error} what="revenue and margin" />;

  const t = data?.total;
  const otherRev = notConvertedLines(t?.revenue);

  return (
    <>
      <GridCell>
        <SectionHeading
          title="Revenue and cost"
          info={data ? `${data.definitions.revenue} ${data.definitions.cost} ${data.definitions.gross_margin}` : undefined}
          help="Revenue = money kept (captured − refunded), VAT-inclusive as charged, Stripe fees not deducted. Cost = vendor cost from the ledgers. Margin = revenue − cost, both at the one FX rate shown above."
        />
        {isLoading ? (
          <div className="mt-4">
            <SkeletonRows />
          </div>
        ) : t ? (
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <div>
              <dt className="text-xs text-muted-foreground">Revenue</dt>
              <dd className="text-lg font-semibold">
                <MoneyText minor={pick(t.revenue, currency)} currency={currency} />
              </dd>
              <dd className="text-[11px] text-muted-foreground">
                Pay as you go {formatMoney(pick(t.revenue_payg, currency), currency)} · Plans{" "}
                {formatMoney(pick(t.revenue_plans, currency), currency)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Cost</dt>
              <dd className="text-lg font-semibold">
                <MoneyText minor={pick(t.cost, currency)} currency={currency} />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Gross margin</dt>
              <dd className="text-lg font-semibold">
                <MoneyText minor={pick(t.gross_margin, currency)} currency={currency} />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Gross margin %</dt>
              <dd className="text-lg font-semibold">
                {t.gross_margin_pct == null ? (
                  <NullValue reason={NULL_REASONS.gross_margin_pct} />
                ) : (
                  <span className={t.gross_margin_pct < 0 ? "text-destructive" : undefined}>{formatPct(t.gross_margin_pct)}</span>
                )}
              </dd>
            </div>
          </dl>
        ) : null}
        {otherRev.length ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Also taken in other currencies, not converted and left out of these totals: {otherRev.join(", ")}.
          </p>
        ) : null}
      </GridCell>

      <GridCell>
        <SectionHeading
          title="Over time"
          help={`Per ${data?.window.groupBy ?? "day"}. Hover a point for that period's margin (which can be a loss).`}
          right={<ChartLegend series={REV_COST} />}
        />
        <div className="mt-3">
          <LinesOverTime
            data={seriesRows}
            series={REV_COST}
            format={fmt}
            tick={tick}
            isLoading={isLoading}
            isEmpty={!anyMoney}
            emptyText="No revenue or cost in this period."
            footer={(row) => (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Gross margin</span>
                <span className={`font-medium tabular-nums ${Number(row.margin) < 0 ? "text-destructive" : ""}`}>
                  {formatMoney(row.margin as number | null, currency)}
                  {row.margin_pct != null ? ` · ${formatPct(row.margin_pct as number)}` : ""}
                </span>
              </div>
            )}
          />
        </div>
      </GridCell>

      <GridCells className="grid-cols-1 xl:grid-cols-2">
        <GridCell>
          <SectionHeading
            title="Revenue vs cost per product"
            help="Meetings and notes covered by a plan show their cost here against product revenue 0; that revenue sits in Plans. Read those with the total."
            right={<ChartLegend series={REV_COST} />}
          />
          <div className="mt-4">
            {isLoading ? (
              <SkeletonRows />
            ) : (
              <PairedBars
                series={REV_COST}
                emptyText="No product had revenue or cost in this period."
                rows={products.map((p) => ({
                  id: p.product,
                  label: p.label,
                  values: { revenue: pick(p.revenue, currency) ?? 0, cost: pick(p.cost, currency) ?? 0 },
                  tip: (
                    <span className="text-xs">
                      <strong>{p.label}</strong>: revenue {fmt(pick(p.revenue, currency) ?? 0)}, cost{" "}
                      {fmt(pick(p.cost, currency) ?? 0)}
                    </span>
                  ),
                }))}
              />
            )}
          </div>
        </GridCell>
        <GridCell>
          <SectionHeading
            title="Gross margin per product"
            help="A bar left of the line is a loss. Ask AI and note reports have no charge of their own, so their cost lowers the total margin."
          />
          <div className="mt-4">
            {isLoading ? (
              <SkeletonRows />
            ) : (
              <DivergingBars
                emptyText="No product had revenue or cost in this period."
                rows={products.map((p) => {
                  const v = pick(p.gross_margin, currency) ?? 0;
                  return {
                    id: p.product,
                    label: p.label,
                    value: v,
                    shown: fmt(v),
                    tip: (
                      <span className="text-xs">
                        <strong>{p.label}</strong>: margin {fmt(v)} (
                        {p.gross_margin_pct == null ? "no revenue, so no %" : formatPct(p.gross_margin_pct)})
                        {p.note ? <span className="mt-1 block text-muted-foreground">{p.note}</span> : null}
                      </span>
                    ),
                  };
                })}
              />
            )}
          </div>
        </GridCell>
      </GridCells>

      <GridCell padded={false}>
        <div className="px-4 py-4 md:px-5">
          <SectionHeading title="Per product" help="Exact values. Hover an amount for the number the API returned." />
        </div>
        {isLoading ? (
          <SkeletonRows />
        ) : !data || data.products.length === 0 ? (
          <EmptyNote>No products in this period.</EmptyNote>
        ) : (
          <DataTable
            caption="Revenue, cost and gross margin per product"
            head={[
              { label: "Product" },
              { label: "Revenue", align: "right" },
              { label: "Payments", align: "right" },
              { label: "Cost", align: "right" },
              { label: "Gross margin", align: "right" },
              { label: "Margin %", align: "right" },
            ]}
          >
            {data.products.map((p) => {
              const other = notConvertedLines(p.revenue);
              return (
                <Fragment key={p.product}>
                  <tr className="border-t border-border/60 align-top">
                    <td className={tdClass}>
                      <span className="font-medium">{p.label}</span>
                      {p.note ? <span className="mt-0.5 block max-w-md text-xs text-muted-foreground">{p.note}</span> : null}
                      {other.length ? (
                        <span className="mt-0.5 block text-xs text-muted-foreground">Not converted: {other.join(", ")}</span>
                      ) : null}
                    </td>
                    <td className={tdNumClass}>
                      <MoneyText minor={pick(p.revenue, currency)} currency={currency} />
                    </td>
                    <td className={tdNumClass}>{formatCount(p.payments)}</td>
                    <td className={tdNumClass}>
                      {p.product === "plans" ? (
                        <span className="text-muted-foreground" title="Plans are revenue only: the cost of plan-covered use is on each product.">
                          –
                        </span>
                      ) : (
                        <MoneyText minor={pick(p.cost, currency)} currency={currency} />
                      )}
                    </td>
                    <td className={tdNumClass}>
                      <MoneyText minor={pick(p.gross_margin, currency)} currency={currency} />
                    </td>
                    <td className={tdNumClass}>
                      {p.gross_margin_pct == null ? (
                        <NullValue reason={NULL_REASONS.gross_margin_pct} className="justify-end" />
                      ) : (
                        <span className={p.gross_margin_pct < 0 ? "text-destructive" : undefined}>{formatPct(p.gross_margin_pct)}</span>
                      )}
                    </td>
                  </tr>
                  {p.by_plan?.map((bp) => (
                    <tr key={bp.plan_code} className="text-xs text-muted-foreground">
                      <td className={`${tdClass} pl-8 md:pl-9`}>{planLabel(bp.plan_code)}</td>
                      <td className={tdNumClass}>
                        <MoneyText minor={pick(bp.revenue, currency)} currency={currency} />
                      </td>
                      <td className={tdNumClass}>{formatCount(bp.invoices)}</td>
                      <td colSpan={3} />
                    </tr>
                  ))}
                  {p.product === "plans" && p.includes_meeting_overage && Object.keys(p.includes_meeting_overage).length ? (
                    <tr className="text-xs text-muted-foreground">
                      <td className={`${tdClass} pl-8 md:pl-9`} colSpan={6}>
                        Meeting overage added to plan invoices in this period:{" "}
                        {Object.entries(p.includes_meeting_overage)
                          .map(([cur, v]) => formatMoney(v, cur.toUpperCase()))
                          .join(", ")}{" "}
                        (it may be paid on a later invoice)
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            {t ? (
              <tr className="border-t-2 border-border font-semibold">
                <td className={tdClass}>Total</td>
                <td className={tdNumClass}>
                  <MoneyText minor={pick(t.revenue, currency)} currency={currency} />
                </td>
                <td className={tdNumClass} />
                <td className={tdNumClass}>
                  <MoneyText minor={pick(t.cost, currency)} currency={currency} />
                </td>
                <td className={tdNumClass}>
                  <MoneyText minor={pick(t.gross_margin, currency)} currency={currency} />
                </td>
                <td className={tdNumClass}>
                  {t.gross_margin_pct == null ? (
                    <NullValue reason={NULL_REASONS.gross_margin_pct} className="justify-end" />
                  ) : (
                    formatPct(t.gross_margin_pct)
                  )}
                </td>
              </tr>
            ) : null}
          </DataTable>
        )}
      </GridCell>
    </>
  );
}
